import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone, ExternalLink, StickyNote, CalendarClock, PackagePlus, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import RatingPicker from './RatingPicker';
import {
  fullName, initials, formatBusinessDate, socialPlatformLabel, socialProfileUrl, todayISODate,
  businessWhatsAppUrl, openBusinessWhatsApp,
} from '../../lib/businessHelpers';
import { logError } from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-xs text-zinc-400">{label}</p>
      <div className="text-sm text-zinc-800">{children}</div>
    </div>
  </div>
);

const ClientDetailDialog = ({ open, onOpenChange, client }) => {
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const clientId = client?.id;
  const today = todayISODate();

  useEffect(() => {
    if (!open || !clientId) { setOrders([]); return; }
    setLoadingOrders(true);
    axios.get(`${API}/business/clients/${clientId}/orders`)
      .then((res) => setOrders(res.data || []))
      .catch((err) => logError('ClientDetailDialog', err))
      .finally(() => setLoadingOrders(false));
  }, [open, clientId]);

  if (!client) return null;
  const profileUrl = socialProfileUrl(client.social_platform, client.social_handle);
  const reminderDates = client.reminder_dates || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-zinc-200 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-lime-100 text-lime-700 flex items-center justify-center font-semibold text-lg flex-shrink-0">
              {initials(client)}
            </div>
            <DialogTitle className="text-left">{fullName(client)}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RatingPicker value={client.rating} variant="dollari" size={18} />

          {client.telefono && (
            <Row icon={Phone} label="Telefono">
              <a
                href={businessWhatsAppUrl(client.telefono)}
                onClick={(e) => { e.preventDefault(); openBusinessWhatsApp(client.telefono); }}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lime-700 hover:underline"
              >
                {client.telefono}
              </a>
            </Row>
          )}

          {client.social_platform && (
            <Row icon={ExternalLink} label="Profilo social">
              {profileUrl ? (
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-lime-700 hover:underline inline-flex items-center gap-1">
                  {socialPlatformLabel(client.social_platform)} {client.social_handle}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span>{socialPlatformLabel(client.social_platform)} {client.social_handle}</span>
              )}
            </Row>
          )}

          {reminderDates.length > 0 && (
            <Row icon={CalendarClock} label={reminderDates.length > 1 ? 'Promemoria' : 'Promemoria'}>
              <ul className="space-y-0.5">
                {reminderDates.map((d) => (
                  <li key={d} className={d <= today ? 'text-orange-700 font-medium' : ''}>
                    {formatBusinessDate(d)}{d <= today ? ' · da ricontattare' : ''}
                  </li>
                ))}
              </ul>
            </Row>
          )}

          {client.note && (
            <Row icon={StickyNote} label="Note">
              <p className="whitespace-pre-wrap">{client.note}</p>
            </Row>
          )}

          <Row icon={PackagePlus} label="Ordini">
            {loadingOrders ? (
              <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
            ) : orders.length === 0 ? (
              <p>Nessun ordine registrato</p>
            ) : (
              <ul className="space-y-2 mt-1">
                {orders.map((o) => (
                  <li key={o.id} className="border border-zinc-200 rounded-lg p-2.5">
                    <p className="text-xs font-medium text-zinc-900">{formatBusinessDate(o.data)}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      {(o.items || []).map((it) => `${it.name} ×${it.qty}`).join(', ')}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Row>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientDetailDialog;
